import React from "react";
import type { CSSProperties } from "react";
import {
  memberPanelStyle,
  memberHeaderStyle,
  avatarPlaceholderStyle,
  memberInfoStyle,
  slotsContainerStyle,
} from "../bookingStyles";

interface OpeningLoadingProps {
  /** Fallback text shown when used outside skeleton context (e.g. submission). */
  text?: string;
  /** Number of skeleton member cards to render. @default 2 */
  cards?: number;
  /** Skip the avatar + name header; render only the slot grid. */
  slotsOnly?: boolean;
}

/* ── Skeleton primitives ── */

const pulseKeyframes = `
@keyframes openings-skeleton-pulse {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}`;

const skeletonBase: CSSProperties = {
  background: "var(--openings-color-border, #e5e5e5)",
  borderRadius: 4,
  animation: "openings-skeleton-pulse 1.5s ease-in-out infinite",
};

const skeletonLine = (
  width: string | number,
  height: number = 12,
): CSSProperties => ({
  ...skeletonBase,
  width,
  height,
});

const skeletonSlot: CSSProperties = {
  ...skeletonBase,
  width: "100%",
  height: "var(--openings-booking-time-button-height, 34px)",
  borderRadius:
    "var(--openings-booking-time-button-radius, var(--openings-booking-radius-sm, 6px))",
};

/* ── Component ── */

const OpeningLoading: React.FC<OpeningLoadingProps> = ({
  text,
  cards = 2,
  slotsOnly = false,
}) => {
  // For non-skeleton uses (e.g. "submitting…" message), keep simple spinner
  if (text) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            ...skeletonBase,
            width: 16,
            height: 16,
            borderRadius: "50%",
          }}
        />
        <span
          style={{
            fontSize: "var(--openings-booking-font-size, 14px)",
            color: "var(--openings-color-text-secondary, #666)",
          }}
        >
          {text}
        </span>
      </div>
    );
  }

  const cardIndices = Array.from({ length: cards }, (_, i) => i);

  return (
    <>
      {/* Inject keyframes once */}
      <style>{pulseKeyframes}</style>

      <div
        className="openings-opening-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--openings-booking-spacing-sm, 8px)",
          width: "100%",
        }}
      >
        {cardIndices.map((i) => (
          <div key={i} style={slotsOnly ? undefined : memberPanelStyle}>
            {!slotsOnly && (
              <div style={memberHeaderStyle}>
                <div
                  style={{
                    ...avatarPlaceholderStyle,
                    ...skeletonBase,
                    background: "var(--openings-color-border, #e5e5e5)",
                  }}
                />
                <div
                  style={{
                    ...memberInfoStyle,
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--openings-booking-spacing-xs, 4px)",
                  }}
                >
                  <div style={skeletonLine("60%", 14)} />
                  <div style={skeletonLine("40%", 10)} />
                </div>
              </div>
            )}

            {/* Slot grid */}
            <div style={slotsContainerStyle}>
              {Array.from({ length: 8 }, (_, j) => (
                <div
                  key={j}
                  className="openings-opening-loading-slot"
                  style={skeletonSlot}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default OpeningLoading;
